// Copyright ©️ 2026 eVolve MEP, LLC

import type { AutodeskDbId } from 'components/Autodesk/types';
import { getBasicStoreListeners } from 'helpers/getBasicStoreListeners';

/**
 * Whether the selected spools can be dimensioned.
 * 'noSelection' - the set of `dimensionableDbIds` is not valid for selection
 * 'measuring' - a measurement to find auto-dimensionable elements is in progress
 * 'supported' - the selection has elements supported by auto dimensioning
 * 'unsupported' - the selection has no auto dimensionable elements.
 */
export type AutoDimensioningStatus = 'noSelection' | 'measuring' | 'supported' | 'unsupported';

type DimensioningSnapshot = {
  /**
   * Tell the extension which elements are eligible for dimensioning. React owns this signal so the extension never
   * needs to know about `SpoolsExtension`. Pass an empty list to clear.
   */
  dimensionableDbIds: Set<AutodeskDbId>;
  /** Whether automatic dimensions are drawn. */
  dimensionsVisible: boolean;
  autoDimensioningStatus: AutoDimensioningStatus;
};

/**
 * External store for the dimensioning extension's UI state. React owns the toggle and publishes what to
 * measure; the extension measures, reports the status back, and does the drawing. Read via the
 * `dimensioning` entry in `useAutodeskExtensionStore`.
 */
export const createDimensioningStore = () => {
  let snapshot: DimensioningSnapshot = {
    dimensionsVisible: false,
    autoDimensioningStatus: 'noSelection',
    dimensionableDbIds: new Set(),
  };

  const { subscribe, notifyListeners } = getBasicStoreListeners();

  const update = (partial: Partial<DimensioningSnapshot>) => {
    snapshot = { ...snapshot, ...partial };
    notifyListeners();
  };

  return {
    getSnapshot: () => snapshot,
    subscribe,
    /** Resets the user-facing state. `dimensionableDbIds` is left alone — React owns it. */
    clear: () =>
      update({ dimensionsVisible: false, autoDimensioningStatus: 'noSelection', dimensionableDbIds: new Set() }),

    /** Show or hide automatic conduit-rack dimensions for the selected spools. */
    setRackDimensionsVisible: (rackDimensionsVisible: boolean) => update({ dimensionsVisible: rackDimensionsVisible }),

    /** Called by the extension as it measures the selection and learns whether it can be dimensioned. */
    setAutoDimensioningStatus: (autoDimensioningStatus: AutoDimensioningStatus) => update({ autoDimensioningStatus }),

    /** Updates the list of dimensionable dbIds */
    setDimensionableElements: (dimensionableDbIdList: readonly AutodeskDbId[]) => {
      const dimensionableDbIds = new Set(dimensionableDbIdList);

      // Bail out if the new list is identical to the old one
      const previousList = snapshot.dimensionableDbIds;
      if (
        [...dimensionableDbIdList].every((dbId) => previousList.has(dbId)) &&
        [...previousList].every((dbId) => dimensionableDbIds.has(dbId))
      ) {
        return;
      }

      update({ dimensionableDbIds: new Set(dimensionableDbIds) });
    },
  };
};

export type DimensioningStore = ReturnType<typeof createDimensioningStore>;
